'use client'

import { Dispatch, MutableRefObject, ReactNode, SetStateAction } from 'react'
import { Badge, Cell, Divider, Image, Radio, Spacer, Text } from '@vinted/web-ui'
import { noop } from 'lodash'
import { InView } from 'react-intersection-observer'

import { getCatalogPhotoSrc, FacetedCategoryDto } from '@marketplace-web/domain/catalog-data'
import { useTracking } from '@marketplace-web/shared/event-tracker'
import {
  userClickDynamicFilterOptionEvent,
  userViewDynamicFilterOptionEvent,
} from '_libs/common/event-tracker/events'
import { getSearchSessionData } from '@marketplace-web/domain/search'
import { Screen } from 'constants/tracking/screens'

import FilterCellSuffix from '../../common/FilterCell/FilterCellSuffix'

type SharedProps = {
  navStack: Array<number>
  setNavStack: Dispatch<SetStateAction<Array<number>>>
  renderNavigation?: (category: FacetedCategoryDto, navigateBack: () => void) => ReactNode
  onClickLeaf?: (category: FacetedCategoryDto) => void
  renderSuffixText?: (category: FacetedCategoryDto) => ReactNode
  selectedIds: Array<number>

  seenItems: MutableRefObject<Set<string>>
  fromScreen: Screen
  filteringSessionId: MutableRefObject<string>
}

type Props = {
  depth?: number
  category: FacetedCategoryDto
  position: number
} & SharedProps

type PropsBase = {
  renderRootNavigation?: () => ReactNode
  categories: Array<FacetedCategoryDto>
} & SharedProps

const CatalogFilterFaceted = ({
  depth = 0,
  category,
  navStack,
  setNavStack,
  renderNavigation,
  onClickLeaf,
  selectedIds,
  renderSuffixText,
  seenItems,
  position,
  filteringSessionId,
  fromScreen,
}: Props) => {
  const { track } = useTracking()

  const navigatedTo = navStack[depth]
  const categoryId = Number(category.id)

  const sharedProps: SharedProps = {
    seenItems,
    renderSuffixText,
    navStack,
    setNavStack,
    onClickLeaf,
    renderNavigation,
    selectedIds,
    filteringSessionId,
    fromScreen,
  }

  const handleNavigate = () => {
    setNavStack(prev => [...prev, categoryId])
  }

  const handleNavigateBack = () => {
    setNavStack(prev => {
      const clone = [...prev]
      clone.pop()

      return clone
    })
  }

  const handleInView = () => {
    if (seenItems.current.has(category.id)) return
    seenItems.current.add(category.id)

    const { globalSearchSessionId, searchSessionId, globalCatalogBrowseSessionId } =
      getSearchSessionData()

    track(
      userViewDynamicFilterOptionEvent({
        position,
        id: category.id,
        itemsCount: category.item_count,
        type: 'catalog',
        code: 'catalog',
        filteringSessionId: filteringSessionId.current,
        globalSearchSessionId,
        globalCatalogBrowseSessionId,
        searchSessionId,
        fromScreen,
      }),
    )
  }

  const handleClickOption = (isSelected: boolean) => {
    const { globalSearchSessionId, searchSessionId, globalCatalogBrowseSessionId } =
      getSearchSessionData()

    track(
      userClickDynamicFilterOptionEvent({
        id: category.id,
        type: 'catalog',
        code: 'catalog',
        position,
        isSelected,
        itemsCount: category.item_count,
        filteringSessionId: filteringSessionId.current,
        globalSearchSessionId,
        globalCatalogBrowseSessionId,
        searchSessionId,
        fromScreen,
      }),
    )
  }

  const handleClick = () => {
    const hasChildren = category.categories.length !== 0
    let isSelected = false

    if (hasChildren) {
      handleNavigate()
    } else {
      onClickLeaf?.(category)
      isSelected = !selectedIds.includes(Number(category.id))
    }

    handleClickOption(isSelected)
  }

  const renderPrefix = () => {
    if (!category.photo) return undefined

    return (
      <div className="u-flexbox u-align-items-center u-fill-height">
        <Image {...getCatalogPhotoSrc(category.photo)} size={Image.Size.Regular} />
      </div>
    )
  }

  if (category.id === '-1') return undefined

  // base case: we reached the end of the navigation stack
  // this means we stop the recursion and display the selectable item
  if (!navigatedTo) {
    const hasChildren = category.categories.length !== 0

    let itemCount: undefined | string
    if (category.item_count != null) {
      const isExceeding = category.item_count > category.max_item_count
      itemCount = isExceeding ? `${category.max_item_count}+` : `${category.item_count}`
    }

    return (
      <InView onChange={inView => inView && handleInView()}>
        <Cell
          prefix={renderPrefix()}
          chevron={hasChildren}
          type={Cell.Type.Navigating}
          suffix={
            <FilterCellSuffix
              suffixText={renderSuffixText?.(category)}
              textTheme="primary"
              badge={
                category.badge ? (
                  <Badge theme={category.badge.theme || 'primary'} content={category.badge.title} />
                ) : undefined
              }
              renderSuffix={() =>
                !hasChildren && (
                  <div className="u-no-pointer-events u-flexbox u-align-items-center">
                    {category.badge && <Spacer orientation={Spacer.Orientation.Vertical} />}
                    <Radio
                      onChange={noop}
                      name={`${category.title}-${categoryId}`}
                      checked={selectedIds.includes(categoryId)}
                      tabIndex={-1}
                      aria={{
                        'aria-labelledby': `title-${categoryId}`,
                      }}
                    />
                  </div>
                )
              }
            />
          }
          onClick={handleClick}
        >
          <Text as="h2" type={Text.Type.Title} text={category.title} id={`title-${categoryId}`} />{' '}
          {itemCount && <Text as="h3" type={Text.Type.Subtitle} text={`(${itemCount})`} />}
        </Cell>
        <Divider />
      </InView>
    )
  }

  // we don't exist in the navigation stack, cut this path entirely
  if (navigatedTo !== categoryId) return undefined

  // we only want to show navigation for the *last* item that exists in the navigation stack
  // otherwise we would have multiple navigation headers for each selected category
  const showNavigation = !navStack[depth + 1]

  return (
    <>
      {showNavigation && (
        <div className="u-sticky-top u-zindex-large u-fill-width">
          {renderNavigation?.(category, handleNavigateBack)}
        </div>
      )}

      {category.categories.map((cat, index) => (
        <CatalogFilterFaceted
          key={cat.id}
          category={cat}
          depth={depth + 1}
          position={index + 1}
          {...sharedProps}
        />
      ))}
    </>
  )
}

const CatalogFilterFacetedBase = ({
  setNavStack,
  navStack,
  categories,
  renderNavigation,
  renderRootNavigation,
  onClickLeaf,
  selectedIds,
  renderSuffixText,
  filteringSessionId,
  fromScreen,
  seenItems,
}: PropsBase) => {
  const sharedProps: SharedProps = {
    seenItems,
    filteringSessionId,
    navStack,
    setNavStack,
    onClickLeaf,
    renderNavigation,
    selectedIds,
    renderSuffixText,
    fromScreen,
  }

  return (
    <>
      {navStack.length === 0 && (
        <div className="u-sticky-top u-zindex-large u-fill-width">{renderRootNavigation?.()}</div>
      )}
      {categories.map((catalog, index) => (
        <CatalogFilterFaceted
          key={catalog.id}
          category={catalog}
          position={index + 1}
          {...sharedProps}
        />
      ))}
    </>
  )
}

export default CatalogFilterFacetedBase
